import {
  AiSparklesGradient,
  Calculator,
  Contract,
  GitBranch,
  InsightsAnalyticsSquare,
  Lightning,
  McpConnector,
  VyCard,
  VyHeroIcon,
  VyIconTile,
  VyIconTileColor,
  VyPage,
  VyStack,
  VyTypography,
} from '@vy/ui-design-system';
import { useState, type ComponentType, type SVGProps } from 'react';
import { VyLayout } from '../../components/new-design/layout/VyLayout';
import { AiCopilotDrawer } from '../../components/ai-copilot/AiCopilotDrawer';
import { AiCopilotProvider } from '../../components/ai-copilot/context/AiCopilotProvider';
import { useAiCopilot } from '../../components/ai-copilot/context/use-ai-copilot';
import { ImportContractDialog } from '../../components/new-design/surfaces/VySearchbar/vy-search-bar-components/ImportContractDialog/ImportContractDialog';

/**
 * "Try Vayu agents" — the AI Agents marketing/launch surface.
 *
 * Reference implementation for the new design system page workflow.
 * Rules in force for this page (see `libs/ui-design-system/PAGES.md`):
 *   - Outermost wrap is `VyLayout` (legacy app chrome: sidebar + topbar).
 *   - `VyPage` from the lib wraps the page CONTENT and establishes the
 *     `VyThemeProvider` boundary. Every UI primitive inside `VyPage` is
 *     a design-system component — no `@mui/material` imports, no `sx`
 *     for visual decisions, no hex literals.
 *   - The legacy interaction surfaces required for the card actions
 *     (`AiCopilotProvider` + `AiCopilotDrawer`, `ImportContractDialog`)
 *     live ALONGSIDE `VyPage`, not inside it — they're legacy app
 *     components, so they sit outside the design-system boundary while
 *     still being inside `VyLayout`. Cards inside `VyPage` only call
 *     `useAiCopilot()` (context flows down) and a `onOpenContractDialog`
 *     callback prop for the dialog.
 *   - This file's job is orchestration: composing primitives, holding
 *     local data, and wiring callbacks. Visual rules live in the lib.
 *
 * The split into `AiAgentsPage` (provider + dialog state) and
 * `AgentsContent` (consumer of `useAiCopilot`) is required because the
 * hook must be called inside a descendant of `AiCopilotProvider`, not
 * inside the same component that mounts the provider.
 */

/**
 * What happens when a card is clicked. Modeled as a discriminated union
 * so the card renderer can dispatch exhaustively. `inactive` cards skip
 * the interactive treatment and show an "Inactive" footer.
 */
type AgentAction =
  | { kind: 'contract-dialog' }
  | { kind: 'open-copilot' }
  | { kind: 'external-link'; href: string }
  | { kind: 'inactive' };

interface Agent {
  id: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  iconColor: VyIconTileColor;
  title: string;
  description: string;
  action: AgentAction;
}

const MCP_DOCS_URL = 'https://guide.withvayu.com/vayu-mcp-server';

const AGENTS: readonly Agent[] = [
  {
    id: 'contract-intake',
    icon: Contract,
    iconColor: 'primary',
    title: 'Contract intake',
    description: 'Extract key terms and create contracts from PDFs',
    action: { kind: 'contract-dialog' },
  },
  {
    id: 'insights',
    icon: InsightsAnalyticsSquare,
    iconColor: 'purple',
    title: 'Insights agent',
    description: 'Turn billing data into insights, trends, and actions',
    action: { kind: 'open-copilot' },
  },
  {
    id: 'mcp',
    icon: McpConnector,
    iconColor: 'warning',
    title: 'MCP connector',
    description: 'Access revenue data easily with Claude connect',
    action: { kind: 'external-link', href: MCP_DOCS_URL },
  },
  {
    id: 'dunning',
    icon: Lightning,
    iconColor: 'warning',
    title: 'Dunning agent',
    description: 'Track overdue invoices and automate payment reminders',
    action: { kind: 'inactive' },
  },
  {
    id: 'workflows',
    icon: GitBranch,
    iconColor: 'neutral',
    title: 'Workflows & Policies',
    description: 'Automate approvals, reviews, and internal processes',
    action: { kind: 'inactive' },
  },
  {
    id: 'revrec',
    icon: Calculator,
    iconColor: 'success',
    title: 'RevRec agent',
    description: 'Manage revenue recognition rules and calculations',
    action: { kind: 'inactive' },
  },
];

const CARD_WIDTH = 322;
const CARD_HEIGHT = 204;

export function AiAgentsPage() {
  const [isContractDialogOpen, setIsContractDialogOpen] = useState(false);

  return (
    // Provider scope is **inside** VyLayout, not around it. The reason:
    // VyLayout renders the topbar (`VySearchBar`) as a sibling of
    // `children`, and the topbar contains the global "Ask Agent" pill
    // (`AiCopilotButton`). That button self-hides when there's no
    // `AiCopilotProvider` ancestor — so by scoping the provider to just
    // our page content + drawer, the topbar pill disappears on this page,
    // avoiding two CTAs that do the exact same thing. Cards inside still
    // get `useAiCopilot()` via this provider.
    //
    // No `suggestions` prop — matches /home/overview (general dashboard
    // context, no page-specific prompt scaffolding).
    <>
      <VyLayout sx={{ padding: 0 }}>
        <AiCopilotProvider>
          <AgentsContent onOpenContractDialog={() => setIsContractDialogOpen(true)} />
          <AiCopilotDrawer />
        </AiCopilotProvider>
      </VyLayout>
      <ImportContractDialog
        open={isContractDialogOpen}
        onClose={() => setIsContractDialogOpen(false)}
      />
    </>
  );
}

interface AgentsContentProps {
  onOpenContractDialog: () => void;
}

function AgentsContent({ onOpenContractDialog }: AgentsContentProps) {
  const { openDrawer } = useAiCopilot();

  return (
    <VyPage>
      <VyStack
        sx={{
          gap: 6,
          alignItems: "center",
          paddingTop: 6,
          paddingBottom: 8,
          paddingX: 3
        }}>
        <VyStack
          sx={{
            gap: 3,
            alignItems: "center",
            maxWidth: 512
          }}>
          <VyHeroIcon>
            <AiSparklesGradient width={32} height={32} />
          </VyHeroIcon>
          <VyStack
            sx={{
              gap: 1,
              alignItems: "center"
            }}>
            <VyTypography variant="h3" align="center">
              Try Vayu agents
            </VyTypography>
            <VyTypography
              variant="body1"
              color="text.secondary"
              align="center"
            >
              Explore specialized agents built to analyze, automate, and
              streamline your workflows.
            </VyTypography>
          </VyStack>
        </VyStack>

        <VyStack
          direction="row"
          sx={{
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "center",
            maxWidth: 1000
          }}>
          {AGENTS.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onOpenContractDialog={onOpenContractDialog}
              onOpenCopilot={openDrawer}
            />
          ))}
        </VyStack>
      </VyStack>
    </VyPage>
  );
}

interface AgentCardProps {
  agent: Agent;
  onOpenContractDialog: () => void;
  onOpenCopilot: () => void;
}

function AgentCard({ agent, onOpenContractDialog, onOpenCopilot }: AgentCardProps) {
  const Icon = agent.icon;
  const isActive = agent.action.kind !== 'inactive';

  // Per-action card props. `external-link` renders as an `<a>` (so
  // Cmd-click / middle-click work natively); other active actions render
  // as `<VyCard interactive>` with role="button". Inactive cards drop
  // all interaction props — the surface stays a plain `<div>`.
  const interactionProps = (() => {
    switch (agent.action.kind) {
      case 'contract-dialog':
        return { interactive: true, onClick: onOpenContractDialog } as const;
      case 'open-copilot':
        return { interactive: true, onClick: onOpenCopilot } as const;
      case 'external-link':
        return {
          interactive: true,
          component: 'a' as const,
          href: agent.action.href,
          target: '_blank',
          rel: 'noopener noreferrer',
        } as const;
      case 'inactive':
        return {} as const;
    }
  })();

  return (
    <VyCard
      {...interactionProps}
      sx={{ width: CARD_WIDTH, height: CARD_HEIGHT }}
      aria-label={agent.title}
    >
      <VyIconTile color={agent.iconColor}>
        {/*
          No explicit width/height — each icon renders at its native SVG
          size. Most card icons are authored 20×20 in Figma; Lightning is
          authored 24×24 (intentionally bigger). Matching Figma exactly.
        */}
        <Icon />
      </VyIconTile>
      <VyStack sx={{
        gap: 1
      }}>
        <VyTypography variant="body1Medium">{agent.title}</VyTypography>
        <VyTypography variant="caption" color="text.secondary">
          {agent.description}
        </VyTypography>
      </VyStack>
      <VyTypography
        variant="caption"
        color={isActive ? 'text.secondary' : 'text.disabled'}
      >
        {isActive ? 'Active' : 'Inactive'}
      </VyTypography>
    </VyCard>
  );
}
